import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { use as echartsUse, init as echartsInit, registerTheme as echartsRegisterTheme } from 'echarts/core';
import { BarChart as echartsBarChart } from 'echarts/charts';
import { TooltipComponent, GridComponent } from 'echarts/components';
import { LabelLayout } from 'echarts/features';
import { SVGRenderer } from 'echarts/renderers';
import { BarChartProps } from './models';
import { formatByValue, tooltipFormatter } from '../utils';
import theme from '../theme.json';
import { convert } from './utils';
import styled from 'styled-components';
import * as styles from './styles';
import onlineTheme from '../../../themes/online';
import { useIsMobile } from '../../../helpers/hooks';

echartsRegisterTheme('sunbit', theme);
// Register the required components
echartsUse([echartsBarChart, SVGRenderer, TooltipComponent, GridComponent, LabelLayout]);

const barSize = 44;
const barGap = 20;
const topBarSize = 50;
const isStacked = (data: string[]) => data.length > 1;
const topPadding = 10;
export const BarChart = React.memo(
  styled(
    ({
      className,
      metadata,
      data,
      direction = 'horizontal',
      seriesProp = {},
      formatter = (value) => value,
    }: BarChartProps) => {
      const chart = useRef(null);
      const isMobile = useIsMobile();
      const LabelAxisMargin = isMobile ? 180 : 250;

      const BarChart = useRef<HTMLDivElement>(null);

      const isDataExists = useMemo(() => !!data, [data]);
      const options = useMemo(() => {
        const converted = convert(data, metadata);

        const valueAxis = {
          type: 'value',
          show: false,
          axisLabel: {
            formatter: (value: number) => formatter(value, metadata.format),
          },
          splitLine: {
            show: false,
          },
        };

        const categoryAxis = {
          type: 'category',
          inverse: direction === 'horizontal',
          data: converted.keys,
          splitLine: {
            show: false,
          },
          axisLine: {
            show: false,
            onZero: false,
          },
          axisTick: {
            show: false,
          },
          axisLabel: {
            fontFamily: 'Poppins',
            fontSize: 14,
            color: onlineTheme.colors.primaryBackground,
            margin: direction === 'horizontal' && LabelAxisMargin,
            overflow: 'break',
            interval: 0,
            width: direction === 'horizontal' ? 180 : 100,
            align: 'left',
            position: 'left',
          },
        };
        const titles = converted.series.map((serie) => serie.name);
        const dataLength = converted.series[0].data.length;
        const series = converted.series.map((serie) => ({
          ...serie,
          type: 'bar',
          stack: 'total',
          barWidth: direction === 'horizontal' ? 44 : 'auto',
          labelLine: false,
          realtimeSort: !isStacked(titles),
          barCategoryGap: 20,
          itemStyle: {
            borderRadius: 6,
          },
          label: {
            show: true,
            position: direction === 'horizontal' ? (isStacked(titles) ? undefined : 'insideLeft') : ['50%', topPadding],
            align: direction === 'horizontal' ? 'left' : 'center',
            distance: 20,
            color: 'white',
            fontWeight: 600,
            fontFamily: 'Poppins',

            formatter: ({ value }: { value: number }) => {
              return formatter(value, metadata.format);
            },
          },
          labelLayout: ({ rect, labelRect }) => {
            const labelSize =
              direction === 'horizontal' ? labelRect.x - rect.x + labelRect.width : labelRect.height + topPadding;
            const containerSize = direction === 'horizontal' ? rect.width : rect.height;
            if (labelSize >= containerSize) {
              return {
                fontSize: 0,
              };
            }
          },

          ...seriesProp,
        }));

        return {
          title: {
            text: metadata.description,
            textStyle: {
              fontFamily: 'Poppins',
            },
          },
          legend: {
            data: titles,
            itemWidth: 20,
            itemHeight: 20,
            right: 20,
            icon: 'circle',
            textStyle: {
              fontFamily: 'Poppins',
            },
          },
          grid: {
            left: LabelAxisMargin,
            right: 0,
            containLabel: false,
            height: direction === 'horizontal' ? dataLength * (barGap + barSize) : 'auto',
          },
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              // Use axis to trigger tooltip
              type: 'shadow', // 'shadow' as default; can also be 'line' or 'shadow'
            },
            formatter: (params) => tooltipFormatter(params, (value: number) => formatter(value, metadata.format)),
          },

          xAxis: direction === 'horizontal' ? valueAxis : categoryAxis,
          yAxis: direction === 'horizontal' ? categoryAxis : valueAxis,
          series: series,
        };
      }, [data, metadata, direction]);

      useEffect(() => {
        let timeout;
        if (BarChart.current && !chart.current) {
          chart.current = echartsInit(BarChart.current, 'sunbit', {
            height: direction === 'horizontal' ? data.length * (barGap + barSize) + topBarSize : undefined,
          });
        }
        if (chart.current) {
          if (!isDataExists) {
            chart.current.showLoading();
          } else {
            chart.current.hideLoading();
            chart.current.setOption(options);
          }
          timeout = setTimeout(() => chart.current.resize(), 1000); // resizing after layout shifts are done hopefully
        }
        return () => {
          if (chart.current) {
            chart.current.dispose();
            chart.current = null;
          }
          if (timeout) clearTimeout(timeout);
        };
      }, [BarChart, options, data]);

      useLayoutEffect(() => {
        const updateSize = (): void => {
          if (chart.current) {
            chart.current.resize();
          }
        };
        window.addEventListener('resize', updateSize);
        return (): void => window.removeEventListener('resize', updateSize);
      }, []);

      return <div ref={BarChart} {...{ className }}></div>;
    }
  )`
    ${styles.BarChart}
  `
);
